/**
 * Loan editor drawer — the rich slide-in editor from
 * `design/Syntra/financing.html` (`#loanDrawer`). Backed by `property_setting`
 * strategy blobs (the same model the Properties page + find-detail use).
 *
 * Sections (per design):
 *   - Status — financing master toggle (Enabled / Disabled = all cash).
 *   - Short-Term / Long-Term tabs — BRRRR only (hard money vs refinance).
 *   - Lender.
 *   - Loan Dates — loan date + computed maturity readout.
 *   - Loan Terms — loan amount via %/$ toggle, rate, term + unit, origination.
 *   - Computed tiles (live), and Holding Costs (BRRRR short-term only).
 *
 * Each view keeps its own draft so toggling tabs never drops in-progress edits.
 * `onSave` receives the strategy-blob patch the parent persists via
 * `useDealSettings`.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { RefreshCw, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { termInMonths } from "@/lib/calculators";
import { cn } from "@/lib/utils";
import type {
  BrrrrSettings,
  FlipSettings,
  LoanInputs,
  RentalSettings,
  Strategy,
} from "@/types/pipeline";

import { FinancingComputedTiles } from "./FinancingComputedTiles";
import { FinancingHoldingCosts } from "./FinancingHoldingCosts";
import { TermField, type TermUnit } from "./TermField";
import type { FinancingInput } from "@/hooks/use-deal-loans";
import type { LoanType } from "@/hooks/use-loans";

import {
  availableViews,
  activeView as resolveActiveView,
  baseForView,
  computeLoanDraftMetrics,
  fmtMaturityDate,
  hasLoanTerms,
  loanViewMeta,
  maturityDateFrom,
  type FinancingSettings,
  type LoanMeta,
  type LoanView,
} from "./financing-metrics";

/** Active loan slot for a strategy + view → loans.loan_type. */
function loanTypeFor(strategy: Strategy, view: LoanView): LoanType {
  if (strategy === "Flip") return "hard_money";
  if (strategy === "Rental") return "conventional";
  return view === "long" ? "refinance" : "hard_money";
}

export interface FinancingSavePatch {
  flip?: FlipSettings;
  brrrr?: BrrrrSettings;
  rental?: RentalSettings;
}

type LoanMode = "pct" | "amt";

interface ViewDraft {
  loanMode: LoanMode;
  ltvPct: number;
  loanAmt: number;
  rate: number;
  /** Canonical term length in months — the source of truth. */
  termMonths: number;
  /** Entry/display unit for the term editor. */
  termUnit: TermUnit;
  points: number;
  loanDate: string;
}

interface LoanEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyAddress: string;
  strategy: Strategy;
  purchase: number;
  arv: number;
  /** Existing strategy blobs — spread on save + used to seed loan terms. */
  settings: FinancingSettings | null;
  /** Financing metadata (lender / enabled / slot) from the loans table. */
  loan?: LoanMeta | null;
  saving?: boolean;
  /** Either arg is null when that table is unchanged — the parent skips its write. */
  onSave: (
    patch: FinancingSavePatch | null,
    financing: FinancingInput | null,
  ) => Promise<void> | void;
}

const FIELD_LABEL =
  "text-[11px] font-bold uppercase tracking-wide text-muted-foreground";
const SECTION_LABEL =
  "text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground";

/**
 * The switch primitive's off state is `bg-input` (#1a1f27) with a
 * `bg-background` thumb (#050608) — near-black on near-black, so a toggled-off
 * loan all but disappears on this drawer's panel. Two minimal changes, no new
 * fill colour to compete with the red "on" state:
 *
 *   - Outline the track while unchecked. The primitive already reserves a 2px
 *     transparent border for the checked state, so filling it in costs no
 *     layout and defines the pill's edge.
 *   - Paint the knob `primary-foreground` (white in both themes) instead of
 *     `background`. It's the same white the checked state already puts on the
 *     red track, and in light mode `--background` is white too — so this is a
 *     dark-mode-only fix that leaves light mode byte-identical.
 */
const TOGGLE_TRACK = cn(
  "data-[state=unchecked]:border-muted-foreground/50",
  "[&>span]:bg-primary-foreground",
);

const VIEW_TAB_LABEL: Record<LoanView, string> = {
  short: "Short-Term",
  long: "Long-Term",
};

function activeLoanFor(
  settings: FinancingSettings | null,
  strategy: Strategy,
  view: LoanView,
): LoanInputs | undefined {
  if (strategy === "Rental") return settings?.rental?.long_term;
  if (strategy === "Flip") return settings?.flip?.hard_money;
  return view === "long"
    ? settings?.brrrr?.long_term
    : settings?.brrrr?.hard_money;
}

/** An untouched slot: every field zero/blank, ready to be filled in. */
function emptyDraft(termUnit: TermUnit): ViewDraft {
  return {
    loanMode: "pct",
    ltvPct: 0,
    loanAmt: 0,
    rate: 0,
    termMonths: 0,
    termUnit,
    points: 0,
    loanDate: "",
  };
}

function buildDraft(
  settings: FinancingSettings | null,
  strategy: Strategy,
  view: LoanView,
  purchase: number,
  arv: number,
): ViewDraft {
  const meta = loanViewMeta(strategy, view);
  const loan = activeLoanFor(settings, strategy, view);
  // Nothing stored for this slot (column is null / `{}` / all zeros) ⇒ show a
  // blank form. Seeding the strategy defaults here read as real, saved loan
  // terms (75% / 7% / 30y / 1pt on an untouched refinance).
  if (!hasLoanTerms(loan)) return emptyDraft(meta.termUnit);
  const ltv = loan?.ltv ?? meta.defaults.ltv;
  const base = baseForView(strategy, view, purchase, arv);
  return {
    loanMode: "pct",
    ltvPct: ltv,
    loanAmt: Math.round(base * (ltv / 100)),
    rate: loan?.rate ?? meta.defaults.rate,
    termMonths: loan
      ? termInMonths(loan, meta.termUnit)
      : meta.termUnit === "years"
        ? meta.defaults.term * 12
        : meta.defaults.term,
    termUnit: loan?.term_unit ?? meta.termUnit,
    points: loan?.points ?? meta.defaults.points,
    loanDate: loan?.loan_date ?? "",
  };
}

/**
 * Draft → hard-money LoanInputs. `term_months` is the canonical truth;
 * legacy `term` stays in months for back-compat.
 */
function draftToHardMoney(draft: ViewDraft, base: number): LoanInputs {
  const amount = currentAmount(draft, base);
  const months = Math.max(1, Math.round(draft.termMonths));
  return {
    ltv:
      base > 0 ? Math.round((amount / base) * 100 * 1e5) / 1e5 : draft.ltvPct,
    rate: draft.rate,
    term: months,
    term_months: months,
    term_unit: draft.termUnit,
    points: draft.points,
    loan_date: draft.loanDate || null,
  };
}

/**
 * Draft → long-term LoanInputs. `term_months` carries the exact term (so mixed
 * terms like 30y6m survive); legacy `term` stays in whole years for back-compat.
 */
function draftToLongTerm(draft: ViewDraft, base: number): LoanInputs {
  const amount = currentAmount(draft, base);
  const months = Math.max(1, Math.round(draft.termMonths));
  return {
    ltv:
      base > 0 ? Math.round((amount / base) * 100 * 1e5) / 1e5 : draft.ltvPct,
    rate: draft.rate,
    term: Math.max(1, Math.round(months / 12)),
    term_months: months,
    term_unit: draft.termUnit,
    points: draft.points,
    loan_date: draft.loanDate || null,
  };
}

/**
 * A draft with nothing filled in. Saving one writes `null` for that loan slot
 * instead of a zero-amount loan (`draftTo*` floors the term at 1 month, which
 * would persist a phantom 1-month loan for a slot the user never touched).
 */
function isEmptyDraft(draft: ViewDraft): boolean {
  return (
    !draft.loanDate &&
    !draft.ltvPct &&
    !draft.loanAmt &&
    !draft.rate &&
    !draft.termMonths &&
    !draft.points
  );
}

function currentAmount(draft: ViewDraft, base: number): number {
  return draft.loanMode === "amt"
    ? Math.max(0, draft.loanAmt)
    : Math.round(base * (draft.ltvPct / 100));
}

export function LoanEditDrawer({
  open,
  onOpenChange,
  propertyAddress,
  strategy,
  purchase,
  arv,
  settings,
  loan,
  saving,
  onSave,
}: LoanEditDrawerProps) {
  const views = useMemo(() => availableViews(strategy), [strategy]);

  const [enabled, setEnabled] = useState(true);
  const [lender, setLender] = useState("");
  const [view, setView] = useState<LoanView>("short");
  // BRRRR only: which loan slot is the active one. null = all cash. The two
  // per-tab toggles drive this in place of the master Status switch.
  const [activeView, setActiveView] = useState<LoanView | null>("short");
  const [drafts, setDrafts] = useState<Record<LoanView, ViewDraft | null>>({
    short: null,
    long: null,
  });
  const [hold, setHold] = useState({ months: 6, taxes: 0, utilities: 100 });
  // Snapshot of the seeded values, so Save can skip a table whose inputs are
  // untouched (avoids no-op PATCHes against property_setting / loans).
  const seeded = useRef<{
    enabled: boolean;
    lender: string;
    activeView: LoanView | null;
    drafts: Record<LoanView, ViewDraft | null>;
    hold: { months: number; taxes: number; utilities: number };
  } | null>(null);

  // Seed everything whenever the drawer opens on a property. Lender / enabled /
  // active slot come from the loans row; loan terms come from settings.
  useEffect(() => {
    if (!open) return;
    const financed = loan ? loan.is_enabled : true;
    const seededView = resolveActiveView(strategy, loan?.loan_type);
    const seededLender = loan?.lender_name ?? "";
    const seededActive = financed ? seededView : null;
    const seededDrafts: Record<LoanView, ViewDraft | null> = {
      short: views.includes("short")
        ? buildDraft(settings, strategy, "short", purchase, arv)
        : null,
      long: views.includes("long")
        ? buildDraft(settings, strategy, "long", purchase, arv)
        : null,
    };
    const seededHold = {
      months: settings?.brrrr?.hold_months ?? 6,
      taxes: settings?.brrrr?.taxes_monthly ?? Math.round(purchase * 0.0018),
      utilities: settings?.brrrr?.utilities_monthly ?? 100,
    };
    setEnabled(financed);
    setLender(seededLender);
    setView(seededView);
    setActiveView(seededActive);
    setDrafts(seededDrafts);
    setHold(seededHold);
    seeded.current = {
      enabled: financed,
      lender: seededLender,
      activeView: seededActive,
      drafts: seededDrafts,
      hold: seededHold,
    };
  }, [open, settings, loan, strategy, purchase, arv, views]);

  const draft = drafts[view];
  const meta = loanViewMeta(strategy, view);
  const base = baseForView(strategy, view, purchase, arv);
  const showTabs = views.length > 1;
  // BRRRR is financed when a tab is active; other strategies use the master toggle.
  const isFinanced = strategy === "BRRRR" ? activeView !== null : enabled;
  const showHolding = strategy === "BRRRR" && view === "short" && isFinanced;

  const patchDraft = (patch: Partial<ViewDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [view]: prev[view] ? { ...prev[view]!, ...patch } : prev[view],
    }));

  const loanAmount = draft ? currentAmount(draft, base) : 0;
  const termMonths = draft ? Math.max(0, Math.round(draft.termMonths)) : 0;

  const metrics = useMemo(
    () =>
      computeLoanDraftMetrics({
        loanAmount,
        annualRatePct: draft?.rate ?? 0,
        termMonths,
        points: draft?.points ?? 0,
        isAmortizing: meta.isAmortizing,
      }),
    [loanAmount, draft?.rate, termMonths, draft?.points, meta.isAmortizing],
  );

  // Holding costs always reflect the SHORT loan's payment (the carry period).
  const shortDraft = drafts.short;
  const shortEmi = useMemo(() => {
    if (!shortDraft) return 0;
    const shortBase = baseForView(strategy, "short", purchase, arv);
    const amt = currentAmount(shortDraft, shortBase);
    const m = Math.max(0, Math.round(shortDraft.termMonths));
    return computeLoanDraftMetrics({
      loanAmount: amt,
      annualRatePct: shortDraft.rate,
      termMonths: m,
      points: shortDraft.points,
      isAmortizing: false,
    }).monthlyEmi;
  }, [shortDraft, strategy, purchase, arv]);

  const maturityDate = fmtMaturityDate(
    maturityDateFrom(draft?.loanDate, termMonths),
  );

  // BRRRR per-tab toggle: turning a tab on makes it the active loan (the other
  // tab turns off automatically — single value), so loan_type follows the view.
  // Turning the active tab off drops the property to all cash.
  const handleActiveToggle = (checked: boolean) => {
    setActiveView(checked ? view : null);
  };

  const handleModeChange = (mode: LoanMode) => {
    if (!draft || mode === draft.loanMode) return;
    if (mode === "amt") {
      patchDraft({ loanMode: "amt", loanAmt: loanAmount });
    } else {
      patchDraft({
        loanMode: "pct",
        ltvPct:
          base > 0 ? Math.round((loanAmount / base) * 1000) / 10 : draft.ltvPct,
      });
    }
  };

  const handleSave = async () => {
    const patch: FinancingSavePatch = {};
    const lenderClean = lender.trim();
    if (strategy === "BRRRR") {
      const short =
        drafts.short ?? buildDraft(settings, strategy, "short", purchase, arv);
      const long =
        drafts.long ?? buildDraft(settings, strategy, "long", purchase, arv);
      // An untouched (all-zero) slot stays unset — see `isEmptyDraft`.
      const hardLoan = isEmptyDraft(short)
        ? undefined
        : draftToHardMoney(
            short,
            baseForView(strategy, "short", purchase, arv),
          );
      const refiLoan = isEmptyDraft(long)
        ? undefined
        : draftToLongTerm(long, baseForView(strategy, "long", purchase, arv));
      patch.brrrr = {
        ...settings?.brrrr,
        hard_money: hardLoan,
        long_term: refiLoan,
        hold_months: Math.max(1, hold.months || 1),
        taxes_monthly: Math.max(0, hold.taxes),
        utilities_monthly: Math.max(0, hold.utilities),
      };
    } else if (strategy === "Flip") {
      const short =
        drafts.short ?? buildDraft(settings, strategy, "short", purchase, arv);
      patch.flip = {
        ...settings?.flip,
        hard_money: isEmptyDraft(short)
          ? undefined
          : draftToHardMoney(short, purchase),
      };
    } else {
      const long =
        drafts.long ?? buildDraft(settings, strategy, "long", purchase, arv);
      patch.rental = {
        ...settings?.rental,
        long_term: isEmptyDraft(long)
          ? undefined
          : draftToLongTerm(long, purchase),
      };
    }
    // Loan terms → property_setting (patch); financing metadata → loans table.
    const financing: FinancingInput = {
      loan_type: loanTypeFor(
        strategy,
        strategy === "BRRRR" ? (activeView ?? view) : view,
      ),
      lender: lenderClean || null,
      enabled: strategy === "BRRRR" ? activeView !== null : enabled,
    };

    // Only write a table whose inputs actually changed since the drawer opened.
    const init = seeded.current;
    const normLender = (s: string | null) => (s ?? "").trim() || null;
    const settingsDirty =
      !init ||
      JSON.stringify(drafts) !== JSON.stringify(init.drafts) ||
      (strategy === "BRRRR" &&
        JSON.stringify(hold) !== JSON.stringify(init.hold));
    const financingDirty =
      !init ||
      !loan || // no row yet → create it
      normLender(lender) !== normLender(init.lender) ||
      (strategy === "BRRRR"
        ? activeView !== init.activeView
        : enabled !== init.enabled);

    if (!settingsDirty && !financingDirty) {
      onOpenChange(false);
      return;
    }
    await onSave(
      settingsDirty ? patch : null,
      financingDirty ? financing : null,
    );
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]"
      >
        <SheetHeader className="space-y-1 border-b border-border px-6 py-4 text-left">
          <SheetTitle className="text-lg font-extrabold">
            Edit Financing
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2 text-[12.5px]">
            <span>
              {propertyAddress} · {strategy}
            </span>
            {isFinanced && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                  BADGE_TONE[meta.badge.tone],
                )}
              >
                {meta.badge.label}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Status — master toggle for non-BRRRR. BRRRR has no master switch;
              its per-tab toggles below own the financed/all-cash state. */}
          {strategy !== "BRRRR" && (
            <div className="flex items-center justify-between">
              <span className={SECTION_LABEL}>Status</span>
              <label className="inline-flex cursor-pointer items-center gap-2 text-[11px] font-semibold text-muted-foreground">
                {enabled ? "Enabled" : "Disabled"}
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  className={TOGGLE_TRACK}
                  aria-label="Enable financing"
                />
              </label>
            </div>
          )}

          {/* Short-Term / Long-Term tabs (BRRRR) — shown even when all cash so a
              loan can be toggled back on. */}
          {showTabs && (
            <div className="inline-flex self-start rounded-full border border-border bg-muted/40 p-0.5">
              {views.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors",
                    view === v
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  aria-pressed={view === v}
                >
                  {v === "short" ? (
                    <Zap className="h-3 w-3" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3 w-3" aria-hidden />
                  )}
                  {VIEW_TAB_LABEL[v]}
                </button>
              ))}
            </div>
          )}

          {/* Per-tab toggle (BRRRR) — selects which loan is active. Only one tab
              is active at a time; the other tab's fields stay visible/editable. */}
          {strategy === "BRRRR" && (
            <div className="flex items-center justify-between">
              <span className={SECTION_LABEL}>
                {VIEW_TAB_LABEL[view]} Financing
              </span>
              <label className="inline-flex cursor-pointer items-center gap-2 text-[11px] font-semibold text-muted-foreground">
                {activeView === view ? "Active" : "Off"}
                <Switch
                  checked={activeView === view}
                  onCheckedChange={handleActiveToggle}
                  className={TOGGLE_TRACK}
                  aria-label={`Activate ${VIEW_TAB_LABEL[view]} financing`}
                />
              </label>
            </div>
          )}

          {isFinanced && draft ? (
            <>
              {/* Lender */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="loan-lender" className={FIELD_LABEL}>
                  Lender
                </Label>
                <Input
                  id="loan-lender"
                  value={lender}
                  onChange={(e) => setLender(e.target.value)}
                  placeholder="Lender name"
                />
              </div>

              {/* Loan dates */}
              <div className="flex flex-col gap-2.5">
                <span className={SECTION_LABEL}>Loan Dates</span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="loan-date" className={FIELD_LABEL}>
                      Monthly Payment Start Date
                    </Label>
                    <DateInput
                      id="loan-date"
                      value={draft.loanDate}
                      onChange={(e) => patchDraft({ loanDate: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className={FIELD_LABEL}>Maturity Date</Label>
                    <div className="flex h-10 items-center rounded-md border border-border bg-muted/40 px-3 text-sm font-bold tabular-nums text-foreground">
                      {maturityDate}
                    </div>
                  </div>
                </div>
              </div>

              {/* Loan terms */}
              <div className="flex flex-col gap-2.5">
                <span className={SECTION_LABEL}>{meta.sectionLabel}</span>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="loan-amount" className={FIELD_LABEL}>
                      Loan
                    </Label>
                    <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
                      {(["pct", "amt"] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => handleModeChange(mode)}
                          className={cn(
                            "rounded px-2 py-0.5 text-[11px] font-bold transition-colors",
                            draft.loanMode === mode
                              ? "bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:text-foreground",
                          )}
                          aria-pressed={draft.loanMode === mode}
                          aria-label={
                            mode === "pct"
                              ? "Percent of value"
                              : "Dollar amount"
                          }
                        >
                          {mode === "pct" ? "%" : "$"}
                        </button>
                      ))}
                    </div>
                  </div>
                  {draft.loanMode === "pct" ? (
                    <div className="relative">
                      <NumberInput
                        id="loan-amount"
                        min={1}
                        max={100}
                        step={0.5}
                        value={draft.ltvPct}
                        onChange={(ltvPct) => patchDraft({ ltvPct })}
                        className="pr-8"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                        %
                      </span>
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                        $
                      </span>
                      <NumberInput
                        id="loan-amount"
                        min={0}
                        step={1000}
                        value={draft.loanAmt}
                        onChange={(loanAmt) => patchDraft({ loanAmt })}
                        className="pl-7"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="loan-rate" className={FIELD_LABEL}>
                      Interest Rate
                    </Label>
                    <div className="relative">
                      <NumberInput
                        id="loan-rate"
                        min={0}
                        step={0.25}
                        value={draft.rate}
                        onChange={(rate) => patchDraft({ rate })}
                        className="pr-14"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                        % p.a.
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className={FIELD_LABEL}>Term</Label>
                    <TermField
                      months={draft.termMonths}
                      unit={draft.termUnit}
                      onChange={({ months, unit }) =>
                        patchDraft({ termMonths: months, termUnit: unit })
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="loan-points" className={FIELD_LABEL}>
                    Origination
                  </Label>
                  <div className="relative">
                    <NumberInput
                      id="loan-points"
                      min={0}
                      step={0.25}
                      value={draft.points}
                      onChange={(points) => patchDraft({ points })}
                      className="pr-12"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                      pts
                    </span>
                  </div>
                </div>
              </div>

              {/* Computed */}
              <div className="flex flex-col gap-2.5">
                <span className={SECTION_LABEL}>Computed</span>
                <FinancingComputedTiles
                  metrics={metrics}
                  termMonths={termMonths}
                />
              </div>

              {/* Holding costs (BRRRR short-term) */}
              {showHolding && (
                <FinancingHoldingCosts
                  state={hold}
                  monthlyEmi={shortEmi}
                  onChange={(patch) =>
                    setHold((prev) => ({ ...prev, ...patch }))
                  }
                />
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/40 p-4 text-center text-[12.5px] text-muted-foreground">
              <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
              Financing is disabled — this property is held in all cash.{" "}
              {strategy === "BRRRR"
                ? "Toggle a loan on to add terms."
                : 'Toggle "Enabled" to add loan terms.'}
            </div>
          )}
        </div>

        <SheetFooter className="gap-2 border-t border-border px-6 py-3.5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const BADGE_TONE: Record<string, string> = {
  hard: "bg-warning/15 text-warning",
  refi: "bg-cyan-500/15 text-cyan-500",
  conv: "bg-blue-500/15 text-blue-500",
  cash: "bg-muted text-muted-foreground",
  other: "bg-muted text-muted-foreground",
};
